// Niveau 1 — Stræk op + hjemmerækkens midte (e i g h r t)

import type { CurriculumTier, Trin } from '../../types';
import { LEVEL_6_KEYS } from './grundniveau';
import { filterWords, fromChunks, fromLetters, literal, round } from './generators';
import { consolidationTrin, pairTrin } from './trinBuilders';

const GN_BASE = [...LEVEL_6_KEYS, ' '];

const t1: Trin = {
  id: 'niveau-1-trin-1',
  tierId: 'niveau-1',
  trinNumber: 1,
  title: 'Op til E og I',
  description: 'Stræk langfingrene op til E og I.',
  goal: 'Stræk langfingrene op til E og I uden at flytte hånden.',
  unlockedKeys: [...GN_BASE, 'e', 'i'],
  newKeys: ['e', 'i'],
  rounds: [
    round({ id: 'niveau-1-trin-1-r1', title: 'Stræk opad', purpose: 'Find E og I.', mode: 'guided', generate: literal('e i e i ei ie e i e i ei ie') }),
    round({ id: 'niveau-1-trin-1-r2', title: 'Forbind til hjemmerække', purpose: 'Skift mellem rækker.', mode: 'guided', generate: literal('de ki de ki ed ik de ki ed ik') }),
    round({ id: 'niveau-1-trin-1-r3', title: 'Hold takten', purpose: 'Skriv én tast pr. slag.', mode: 'recall', generate: literal('lide fare dele eje is file ide eje'), presentation: { kind: 'metronome', bpm: 55 } }),
    round({ id: 'niveau-1-trin-1-r4', title: 'Blanding', purpose: 'Tilfældig rækkefølge.', mode: 'recall', generate: fromLetters(['a', 's', 'd', 'f', 'j', 'k', 'l', 'æ', 'e', 'i'], 3, 80) }),
    round({ id: 'niveau-1-trin-1-r5', title: 'Mini-udfordring', purpose: 'Ingen hjælp.', mode: 'challenge', generate: fromChunks(['lide', 'is', 'fare', 'dele', 'eje', 'ide', 'file', 'fart', 'rist', 'rede'], 80) }),
  ],
};

let prior = t1.unlockedKeys;

const t2 = pairTrin(
  'niveau-1',
  2,
  'Hjemmerækkens midte',
  'Tilføj G og H mellem ankrene.',
  'Midtertasterne G og H uden at løfte hænderne.',
  ['g', 'h'],
  prior,
  {
    intro: 'g h g h gh hg g h g h gh hg',
    connect: 'fg hj dg kh fg hj dg kh fg hj',
    recall1: 'fg jh gh fj dg kh fg jh gh fj',
    recall2: 'gh fj dg kh fg hj gh fj dg kh',
    challenge: 'fgjh ghjk dghj fghj hjfg khdg',
  },
  { palette: 'metronome' },
);
prior = t2.unlockedKeys;

const t3 = pairTrin(
  'niveau-1',
  3,
  'R og T',
  'Pegefingeren strækker op til R og T.',
  'Ram R og T med pegefingrene uden at flytte håndfladen.',
  ['r', 't'],
  prior,
  {
    intro: 'r t r t rt tr r t r t rt tr',
    connect: 'fr jt dr kt fr jt dr kt fr jt',
    recall1: 'rt fr jt dr kt rt fr jt dr',
    recall2: 'tr fr jt dr kt tr fr jt dr',
    challenge: 'frjt drkt rtfr jtdr ktrf',
  },
  { palette: 'metronome' },
);
prior = t3.unlockedKeys;

const NIVEAU_1_WORD_POOL = [
  'er', 'de', 'det', 'her', 'der', 'ret', 'tid', 'rig', 'rart',
  'side', 'ride', 'red', 'leder', 'lide', 'dele', 'eje', 'ide',
  'har', 'tag', 'grad', 'fred', 'gift', 'hest', 'rest',
  'fare', 'fart', 'file', 'rist', 'rite', 'rede',
  // Navne (dictation-names.json)
  'freja', 'gitte', 'hjalte', 'ida', 'jette', 'lærke', 'rikke', 'stig', 'tage',
];

export const NIVEAU_1_SPRINT_WORDS = filterWords(NIVEAU_1_WORD_POOL, prior);

const t4 = consolidationTrin(
  'niveau-1',
  4,
  'Konsolidering',
  'Bland de nye taster i korte ord.',
  'Skriv flydende med E, I, G, H, R og T.',
  prior,
  NIVEAU_1_SPRINT_WORDS,
  'det er rart at ride her',
);

export const NIVEAU_1_UNLOCKED_KEYS = prior;

export const NIVEAU_1_TRINS: Trin[] = [t1, t2, t3, t4];

export const NIVEAU_1_TIER: CurriculumTier = {
  id: 'niveau-1',
  title: 'Niveau 1',
  description: 'Stræk op til E og I, hjemmerækkens midte, og R og T.',
  trins: NIVEAU_1_TRINS,
};
